import React, { useEffect, useState } from 'react';
import {
    Drawer,
    List,
    Tooltip,
    Typography,
    Box,
    ListProps,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import gatewayLogo from '../../../assets/gateway-logo-red.svg';
import graceLogo from '../../../assets/grace_information-logo-red.svg';
import { useHistory } from "react-router-dom";
import { useGetCartQuery, useGetUserInfoQuery } from "../../../services/grace";
import CartItemDialog from "../../dialogs/CartItemDialog/CartItemDialog";
import { drawerWidth } from "../../../util/globals";
import ChangePasswordDialog from "../../dialogs/ChangePasswordDialog/ChangePasswordDialog";
import { useAlertDispatch } from '../../../context/AlertContext/AlertContext';
import AppDrawerButton from "../AppDrawerButton/AppDrawerButton";

enum AppModes {
    Normal = 'normal',
    AddMaterials = 'addMaterials',
    AddServices = 'addServices',
    ChangePassword = 'changePassword'
}

declare type AppMode = AppModes.Normal | AppModes.AddMaterials | AppModes.AddServices | AppModes.ChangePassword;

const CustomSidebar = styled(List)<ListProps>(({ theme }) => ({
    "& .MuiButtonBase-root": {
        paddingLeft: '40px',
    }, 
}));

export default function AppDrawer(): JSX.Element {
    const history = useHistory();
    const alertDispatch = useAlertDispatch();

    const [forcePasswordChange, setForcePasswordChange] = useState(false);

    // Errors for userData are caught in App.tsx
    const {
        data: userData,
        refetch: refetchUserData,
    } = useGetUserInfoQuery();

    const [loginText, setLoginText] = useState("");

    useEffect(() => {
        setLoginText(
            (userData !== undefined && userData.lastLogin !== undefined)
                ? `Last login: ${userData?.lastLogin}`
                : ""
        );

        if (userData !== undefined && userData.pwChangeRequired) {
            setAppMode(AppModes.ChangePassword)
            setForcePasswordChange(true);
        }
    }, [userData])

    // Only load the cart if the user has access to it
    const loadCart = userData === undefined ? false : (
        userData.permissions.orderItems
        || userData.permissions.orderMaterials
        || userData.permissions.orderServices
    );

    // Catch cart data load issues in this file
    const {
        data: cartData,
        error: cartError,
        isFetching: cartIsFetching,
        isError: cartIsError,
    } = useGetCartQuery(null, { skip: !loadCart });

    const [cartBadge, setCartBadge] = useState("");

    useEffect(() => {
        if (cartIsError) {
            setCartBadge("!");
            alertDispatch({
                type: 'add',
                payload: {
                    message: "Failed to load cart data",
                }
            })
        } else if (cartIsFetching || cartData === undefined) {
            setCartBadge("?");
        } else {
            setCartBadge(String(cartData.length));
        }
    }, [cartData, cartIsFetching, cartIsError, cartError, alertDispatch])

    const [appMode, setAppMode] = useState<AppMode>(AppModes.Normal);

    const handleItemClick = (event: React.MouseEvent<HTMLElement>) => {
        switch (event.currentTarget.dataset.index) {
            case "logo":
            case "home": {
                if (history.location.pathname !== "/") {
                    history.push("/");
                }
                break;
            }

            case "inventory":
            case "search":
            case "cart":
            case "order":
            case "addresses":
            case "logout": {
                if (history.location.pathname !== `/${event.currentTarget.dataset.index}`) {
                    history.push(`/${event.currentTarget.dataset.index}`);
                }
                break;
            }

            case "materials": {
                setAppMode(AppModes.AddMaterials);
                break;
            }

            case "services": {
                setAppMode(AppModes.AddServices);
                break;
            }

            case "changePassword": {
                setAppMode(AppModes.ChangePassword);
                break;
            }

            case "exporter":
            case "sod":
            case "safepath":
            case "analytix": {
                window.open(`/api/services/${event.currentTarget.dataset.index}`);
                break;
            }
        }
    }

    const handleAddMaterialsClose = () => setAppMode(AppModes.Normal);

    const handleAddMaterialsSubmit = () => setAppMode(AppModes.Normal);

    const handleAddServicesClose = () => setAppMode(AppModes.Normal);

    const handleAddServicesSubmit = () => setAppMode(AppModes.Normal);

    const handleChangePasswordClose = () => setAppMode(AppModes.Normal);

    const handleChangePasswordSubmit = () => {
        setForcePasswordChange(false);
        setAppMode(AppModes.Normal);
        // Force a refetch of user data to ensure
        // the password change flag is reset
        refetchUserData();
    }

    return (
        <Box>
            <CartItemDialog
                open={appMode === AppModes.AddMaterials}
                type='material'
                mode='add'
                cartItems={[]}
                itemTypes={userData?.materials}
                locationTypes={userData?.gLocations}
                onClose={handleAddMaterialsClose}
                onSubmit={handleAddMaterialsSubmit}
            />

            <CartItemDialog
                open={appMode === AppModes.AddServices}
                type='service'
                mode='add'
                cartItems={[]}
                itemTypes={userData?.services}
                locationTypes={userData?.gLocations}
                onClose={handleAddServicesClose}
                onSubmit={handleAddServicesSubmit}
            />

            <ChangePasswordDialog
                open={appMode === AppModes.ChangePassword}
                preventClose={forcePasswordChange}
                onClose={handleChangePasswordClose}
                onSubmit={handleChangePasswordSubmit}
            />

            <Drawer
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    '& .MuiDrawer-paper': {
                        width: drawerWidth,
                        boxSizing: 'border-box',
                    },
                }}
                variant="permanent"
                anchor="left"
            >
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    padding: "20px"
                }}
                     data-index="logo"
                     onClick={handleItemClick}
                >
                    <img src={gatewayLogo} alt="Gateway Logo" style={{maxWidth: "80%"}} />
                </Box>
                <CustomSidebar sx={{flexGrow: 1}}>
                    {(userData?.permissions.orderItems || userData?.permissions.orderMaterials || userData?.permissions.orderServices) &&
                        <AppDrawerButton
                            dataIndex={'cart'}
                            onClick={handleItemClick}
                            text="Cart"
                            icon={['fas', 'shopping-cart']}
                            badgeContent={<Typography fontWeight="bold" color="#db291d" display="inline">({cartBadge})</Typography>}
                        />
                    }
                    {userData?.permissions.orderMaterials &&
                        <AppDrawerButton dataIndex={'materials'} onClick={handleItemClick} text="+ Materials" icon={['fas', 'boxes']}/>
                    }
                    {userData?.permissions.orderServices &&
                        <AppDrawerButton dataIndex={'services'} onClick={handleItemClick} text="+ Services" icon={['fas', 'person-carry']}/>
                    }
                    {userData?.permissions.search &&
                        <AppDrawerButton dataIndex={'inventory'} onClick={handleItemClick} text="Inventory" icon={['fas', 'warehouse-alt']}/>
                    }
                    {userData?.permissions.orderStatus &&
                        <AppDrawerButton dataIndex={'order'} onClick={handleItemClick} text="Orders" icon={['fas', 'forklift']}/>
                    }
                    {userData?.permissions.manageAddresses && true &&
                        <AppDrawerButton dataIndex={'addresses'} onClick={handleItemClick} text="Addresses" icon={['fas', 'address-book']}/>
                    }
                    <AppDrawerButton dataIndex={'exporter'} onClick={handleItemClick} text="File Exporter" icon={['fas', 'file-export']}/>
                    <AppDrawerButton dataIndex={'sod'} onClick={handleItemClick} text="Scan on Demand" icon={['fas', 'scanner-image']}/>
                    <AppDrawerButton dataIndex={'safepath'} onClick={handleItemClick} text="SafePath"/>
                    <AppDrawerButton dataIndex={'analytix'} onClick={handleItemClick} text="AnalytiX"/>
                </CustomSidebar>
                <Box sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    padding: "30px"
                }}
                     data-index="logo"
                     onClick={handleItemClick}
                >
                    <img src={graceLogo} alt="Grace Logo" style={{maxWidth: "80%"}} />
                </Box>
            </Drawer>
        </Box>
    );
}
